/**
 * Centralized API utility for authenticated requests
 *
 * All API calls should use these helpers to ensure JWT tokens are included,
 * expired access tokens are refreshed automatically, and auth errors are handled consistently.
 */

import { API_BASE_URL } from '../config';

let refreshInFlight = null;

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Concurrent callers share one in-flight refresh request.
 * @returns {Promise<boolean>}
 */
export const tryRefreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return false;
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('accessToken', data.access_token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

/**
 * Get authorization headers for API calls
 * @returns {Object} Headers object with Authorization if token exists
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const forceLogout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
};

/**
 * Handle authentication errors (401, 403)
 * @param {Response} response - Fetch response object
 * @param {{ allowRefresh?: boolean, requestUrl?: string }} options
 * @returns {Promise<boolean>} true if caller should retry the request
 */
const handleAuthError = async (response, { allowRefresh = false, requestUrl = '' } = {}) => {
  if (response.status === 401) {
    const isRefreshCall = requestUrl.includes('/api/auth/refresh');
    if (allowRefresh && !isRefreshCall) {
      const refreshed = await tryRefreshAccessToken();
      if (refreshed) {
        return true;
      }
    }
    forceLogout();
    throw new Error('Authentication required. Please login again.');
  }

  if (response.status === 403) {
    throw new Error('You do not have permission to perform this action.');
  }

  return false;
};

/**
 * Authenticated fetch wrapper with timeout, retry logic, and token refresh
 *
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds (default: 30000)
 * @param {number} retries - Number of retry attempts (default: 2)
 * @param {boolean} allowRefreshRetry - Whether a 401 may trigger one token refresh + retry
 * @returns {Promise<Response>}
 */
export const authenticatedFetch = async (
  url,
  options = {},
  timeout = 30000,
  retries = 2,
  allowRefreshRetry = true
) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const headers = {
        ...getAuthHeaders(),
        ...(options.headers || {}),
      };

      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 401) {
        const shouldRetry = await handleAuthError(response, {
          allowRefresh: allowRefreshRetry,
          requestUrl: url,
        });
        if (shouldRetry) {
          return authenticatedFetch(url, options, timeout, retries, false);
        }
      }

      if (response.status === 403) {
        await handleAuthError(response);
      }

      return response;
    } catch (error) {
      if (
        error.message?.includes('Authentication') ||
        error.message?.includes('permission')
      ) {
        throw error;
      }

      if (attempt === retries) {
        if (error.name === 'AbortError') {
          throw new Error(`Request timed out after ${timeout}ms`);
        }
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
};

/**
 * Convenience method for GET requests
 */
export const apiGet = async (path, options = {}) => {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  return authenticatedFetch(url, {
    method: 'GET',
    cache: 'no-store',
    ...options,
  });
};

/**
 * Convenience method for POST requests
 */
export const apiPost = async (path, data, options = {}) => {
  const { timeout = 30000, ...fetchOptions } = options;
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  return authenticatedFetch(
    url,
    {
      method: 'POST',
      body: JSON.stringify(data),
      ...fetchOptions,
    },
    timeout
  );
};

/**
 * Convenience method for PUT requests
 */
export const apiPut = async (path, data, options = {}) => {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  return authenticatedFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * Convenience method for DELETE requests
 */
export const apiDelete = async (path, options = {}) => {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  return authenticatedFetch(url, {
    method: 'DELETE',
    ...options,
  });
};

/**
 * Convenience method for PATCH requests
 */
export const apiPatch = async (path, data, options = {}) => {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  return authenticatedFetch(url, {
    method: 'PATCH',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * Extract a human-readable error message from a FastAPI / API JSON body.
 */
export const parseApiError = (data, fallback = 'Request failed') => {
  if (!data || typeof data !== 'object') return fallback;
  if (data.error) return String(data.error);
  if (data.message) return String(data.message);
  const detail = data.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || d.message || JSON.stringify(d)).join(', ');
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return fallback;
};

export { API_BASE_URL };
